/**
 * המנוי של הארגון — what was purchased and what is used. 08 §6.0.1, §7, §12B.3.
 *
 * ── THIS SCREEN IS REACHABLE IN EVERY SUBSCRIPTION STATE, INCLUDING `suspended` ─────────────────
 *
 * 08 §6.0.1 makes billing screens exempt from the subscription gate, and the reason is a bug that
 * was already written once: without the exemption the gate returned 402 on the ONE screen whose
 * purpose is to resolve the 402 — and the Hebrew denial that says "speak to the office manager about
 * the subscription" pointed at a screen the office manager was also denied.
 *
 * So a suspended office still sees this. It sees WHY, and it sees exactly what it bought.
 *
 * ── WHAT IS NOT HERE ────────────────────────────────────────────────────────────────────────────
 *
 * No price, no invoice total, no discount. 08 §8.4 / CF-66: two tenants identical in plan, SKUs,
 * assignments and state and differing ONLY in a discount must produce byte-identical decisions —
 * *"if changing the price invalidated the entitlement cache, price would be part of entitlement."*
 * Money lives in the billing surface; capacity and entitlement live here, and the boundary between
 * them is the thing being protected.
 *
 * O-3 governs the rest: a subscription screen states what the office has. It does not sell.
 */

import { AppShell } from '../shell/AppShell';
import { useLocale } from '../i18n/LocaleProvider';
import { useCallback } from 'react';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityPanel } from '../components/CapacityPanel';
import { FactList } from '../components/FactList';
import { PlanChangePanel } from '../components/PlanChangePanel';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { formatInstantHe, subscriptionStateHe } from '../api/adminVocabulary';
import { ServerRead } from '../components/ServerRead';
import { useServerData } from '../api/useServerData';
import { callOperation } from '../api/operations';
import { routes } from '../routes';


export function TenantSubscriptionScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical B.**
   *
   * This screen used to render `fixtureSubscription`, which meant WHAT AN OFFICE IS TOLD IT IS
   * PAYING FOR — its plan, its state, its seat and company ceilings, which modules it holds — was
   * development data. An office reading `פעיל` off a fixture while its real subscription was
   * `past_due` would learn otherwise from a 402 on a screen it was in the middle of using.
   *
   * The route is `subscriptionExempt`, deliberately: 08 §6.0.1 keeps billing readable in EVERY
   * state, including suspended, because a customer who cannot pay must still be able to see why.
   * That exemption is worth nothing while the screen reads a local constant, and this is where it
   * starts being worth something.
   *
   * A 404 is `no_subscription` and is NOT the same as a refused read — `<ServerRead>` renders the
   * second, and the first arrives as `notFound` inside it with its own Hebrew.
   */
  const read = useCallback(() => callOperation('tenant.billing.read', {}), []);
  const subscriptionRead = useServerData(read);
  /*
   * THE QUOTAS ARE A SECOND READ, AND THAT IS THE FIX. **2026-11-02.**
   *
   * They used to be a field of the payload above -- declared in the web contract, emitted by
   * nothing anywhere in the tree -- so the panel was handed `undefined` and this screen rendered as
   * a fault boundary on the Owner's estate.
   *
   * Making it its own read is not plumbing. Usage is a LIVE COUNT over companies and seats, which
   * is not a column of the subscription row, and a read of its own is the only way the screen can
   * say four different things: it is still loading, there is no live subscription so there are no
   * limits, the read failed, or here are the numbers. One optional array could say none of them --
   * every one of those states would have arrived as the same empty value.
   */
  const readCapacity = useCallback(() => callOperation('tenant.capacity.read', {}), []);
  const capacityRead = useServerData(readCapacity);
  const back = { labelHe: t('settings.title'), href: routes.tenantAdmin };

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('nav.subscription')} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('nav.subscription')}
      backToParent={back}
    >
      <ServerRead
        result={subscriptionRead.result}
        whatHe={t('sub.reading')}
        skeletonRows={5}
        // 404 here is `no_subscription`, which is a state an office can actually be in — not an
        // error. It gets the words it had before this screen read anything.
        renderNotFound={() => (
          <StateBlock
            titleHe={t('sub.emptyTitle')}
            bodyHe={t('sub.emptyBody')}
            tone="unavailable"
            nextStep={<p>{t('common.persistsContactSupport')}</p>}
          />
        )}
      >
        {(subscription) => {
          const state = subscriptionStateHe(subscription.state);
          return (
            <>
              <FactList
                facts={[
                  { labelHe: t('sub.plan'), value: subscription.planNameHe },
                  { labelHe: t('sub.state'), value: <StatusChip labelHe={state.labelHe} tone={state.tone} /> },
                  {
                    labelHe: t('sub.period'),
                    value: (
                      <span dir="ltr">
                        {formatInstantHe(subscription.periodStartUtc)} — {formatInstantHe(subscription.periodEndUtc)}
                      </span>
                    ),
                  },
                  {
                    labelHe: t('sub.trialEnds'),
                    value: formatInstantHe(subscription.trialEndsAtUtc),
                    absentHe: t('sub.noTrial'),
                  },
                ]}
              />

              {/*
                FOUR OUTCOMES, FOUR SENTENCES. `<ServerRead>` owns loading and failure; the 404 is
                `subscription_not_found`, which is a real state of a real office and NOT a quota of
                zero; and `CapacityPanel` owns the difference between "no rows were recorded" and
                "the rows never arrived".
              */}
              {/*
                `data-area` NAMES THE REGION so an assertion about the quotas is about the quotas.
                The first regression case asked the whole page whether anything had failed, which is
                a question about the shell as much as about this read.
              */}
              <div data-area="quotas">
              <ServerRead
                result={capacityRead.result}
                whatHe={t('sub.readingQuotas')}
                skeletonRows={2}
                renderNotFound={() => (
                  <StateBlock
                    titleHe={t('sub.quotas')}
                    bodyHe={t('sub.quotasNoPlan')}
                    tone="unavailable"
                  />
                )}
              >
                {(data) => <CapacityPanel capacity={data.capacity} captionHe={t('sub.quotas')} />}
              </ServerRead>
              </div>

              <section className="tiles">
                {ctx.modules.map((m) => {
                  const purchased = subscription.licensedModules.includes(m.moduleCode);
                  return (
                    <article key={m.moduleCode} className="tile" data-available={purchased ? 'true' : 'false'}>
                      <h2 className="tile__title">{m.displayNameHe}</h2>
                      {/*
                        Purchased is not the same as available: the server resolves
                        Subscription → SKU → sku_module → module_code, and a company can be in the plan and
                        out of scope for one module. Both facts are shown, and neither is computed here.
                      */}
                      <p className="tile__lead">{purchased ? t('sub.purchased') : t('sub.notPurchased')}</p>
                      {m.catalogueStatus === 'active' ? null : (
                        <p className="tile__unavailable">{t('state.notOpenedYet')}</p>
                      )}
                    </article>
                  );
                })}
              </section>

              <StateBlock
                titleHe={t('sub.changeTitle')}
                bodyHe={t('sub.changeBody')}
                tone="unavailable"
                nextStep={<p>{t('sub.changeNext')}</p>}
              />
            </>
          );
        }}
      </ServerRead>

      {/*
        ── THE PLAN CHANGE IS ON THIS SCREEN NOW, AND IT IS OUTSIDE THAT READ. **ITEM 4.** ──────

        The block inside SAID it was not: *"שינוי תכנית נעשה מול הפלטפורמה ואינו מתבצע במסך הזה"*,
        which was true until today. What is still true -- raising a quota is the platform's -- is
        what it says now.

        **OUTSIDE `<ServerRead>`, AND THAT PLACEMENT IS THE FIX RATHER THAN A PREFERENCE.**
        `onChanged` re-reads the subscription so the plan and the period above show the move. A
        panel mounted INSIDE that read is unmounted while it reloads, and the state holding
        *"המעבר נרשם לסוף התקופה"* goes with it — the office changes its plan and the screen says
        nothing. That is the third time this product has met that defect (the cancellation
        confirmation, the settlement result, and this), and the first draft of this panel carried
        a comment claiming it had avoided it: the dom suite mounts the panel alone and could not
        see it, and the browser showed it on the first press.

        THE PANEL NEEDS NOTHING FROM THAT READ. It has its own, so the coupling was never real.
      */}
      <PlanChangePanel onChanged={subscriptionRead.reload} />
    </AppShell>
  );
}
